import PropTypes from 'prop-types';
import React from 'react';
import shortid from 'shortid';
import { EmitMetric } from '../../../components/Analytics/VisibilityPixel/VisibilityPixel';
import FGBLabel from '../../../components/Elements/FGBLabel/FGBLabel';
import Button from '../../../components/Input/Button/Button';
import './EmployerLandingPage.scss';

import EmployerPageLayout from '../../../components/Employer/Layout/EmployerPageLayout/EmployerPageLayout';
import AlternatingImageTextGrid from '../../../components/Layout/AlternatingImageTextGrid/AlternatingImageTextGrid';
import ResourceContentCardGrid from '../../../components/Employer/Layout/ResourceContentCardGrid/ResourceContentCardGrid';
import {
    FgbHelpsEmployerSectionProps,
    EmployerBrandingProgramProps,
    byTheNumbersSection,
    pressLogos,
} from './StaticProps';
import PressLogoGrid from '../../../components/Layout/PressLogoGrid/PressLogoGrid';
import CustomerQuotesSection from '../../../components/Employer/Marketing/CustomerQuotesSection/CustomerQuotesSection';
import VideoSection from '../../../components/Employer/Marketing/VideoSection/VideoSection';
import EmployerLogos from '../../../components/Employer/Marketing/EmployerLogos/EmployerLogos';
import ResponsivePicture from '../../../components/Elements/ResponsivePicture/ResponsivePicture';
import {
    SnowplowPageViewTracker,
    PAGE_VIEW_SECTIONS,
    PAGE_VIEW_TYPES,
    PAGE_TABS,
} from '../../../components/Analytics/Snowplow/SnowplowPageViewTracker';

const headerFooterImageBreakpoints = {
    desktop: { width: 1500 },
    tablet: { width: 992 },
    smallTablet: { width: 768 },
    mobile: { width: 575 },
};

const sectionImageBreakpoints = {
    mobile: {
        width: 80,
        height: 80,
        transforms: {
            crop: 'scale',
            background: 'transparent',
        },
    },
};

export class EmployerLandingPage extends React.PureComponent {
    handleEmitMetric = () => {
        EmitMetric({
            misc_event_type: 'employer-homepage-company-stories-click',
            misc_event_count: 1,
        });

        return false;
    };

    topImage = () => {
        const { handleDemoButtonClick } = this.props;
        return (
            <section className="top-image-section">
                <div className="wrapper-div">
                    <ResponsivePicture
                        cloudinaryID="/v1/EmployerHomepage/Header_Image_For_Employers_Lander.jpg"
                        forceImageSize
                        breakpoints={headerFooterImageBreakpoints}
                    />
                </div>
                <div className="white-call-to-action">
                    <h1 className="text">
                        Where millions of women are finding their next job
                    </h1>
                    <div className="button-div">
                        <Button
                            type="solid"
                            value="Learn more"
                            onClick={handleDemoButtonClick(
                                'employer-page-popup-click'
                            )}
                        />
                    </div>
                </div>
            </section>
        );
    };

    employerLogoSection = () => {
        const { companies } = this.props;
        return (
            <section className="employer-logo-section">
                <EmployerLogos companies={companies} />
            </section>
        );
    };

    fgbHelpsEmployerSection = () => {
        const { handleDemoButtonClick } = this.props;
        return (
            <section className="fgb-helps-employer-section light-shadow">
                <h2 className="title">
                    Fairygodboss Helps Companies Increase Gender Diversity
                </h2>
                <div className="controlled-width">
                    {FgbHelpsEmployerSectionProps.map((section) => {
                        const sectionKey = shortid();
                        return (
                            <div key={sectionKey} className="section">
                                <ResponsivePicture
                                    className="helps-employer-icon"
                                    cloudinaryID={section.img}
                                    breakpoints={sectionImageBreakpoints}
                                />
                                <FGBLabel className="black-font">
                                    {section.label}
                                </FGBLabel>
                                <p className="description">
                                    {section.description}
                                </p>
                            </div>
                        );
                    })}
                </div>
                <div className="btn-div">
                    <Button
                        type="solid"
                        value="Learn more"
                        onClick={handleDemoButtonClick(
                            'employer-page-popup-click'
                        )}
                    />
                </div>
            </section>
        );
    };

    featuredVideoSection = () => {
        const { videoSrc } = this.props;

        if (!videoSrc || videoSrc.length === 0) return null;

        return (
            <section className="employer-video light-shadow">
                <VideoSection videoSrc={videoSrc} />
            </section>
        );
    };

    marketingBlogSection = () => {
        const { handleDemoButtonClick } = this.props;
        return (
            <section className="employer-branding-program-section light-shadow">
                <FGBLabel className="main-fgb-label">Features</FGBLabel>
                <h2 className="section-header">
                    A 360-Degree Recruiting & Employer Branding Program
                </h2>
                <p className="section-description">
                    Female job seekers don’t look for jobs the same way men do.
                    Here’s how we help.
                </p>
                <AlternatingImageTextGrid
                    items={EmployerBrandingProgramProps}
                />
                <div className="btn-div">
                    <Button
                        type="solid"
                        value="Learn more"
                        onClick={handleDemoButtonClick(
                            'employer-page-popup-click'
                        )}
                    />
                </div>
            </section>
        );
    };

    customerStoriesSection = () => {
        const { resources } = this.props;

        if (!resources || resources.length === 0) return null;

        return (
            <section className="customer-stories-section light-shadow">
                <h2 className="section-header">Customer Stories</h2>
                <p className="section-description">
                    Fairygodboss is helping top companies reach female job
                    seekers.
                </p>
                <ResourceContentCardGrid
                    resourceContent={resources}
                    cardAlignment="center-align"
                    onResourceClick={this.handleEmitMetric}
                />
            </section>
        );
    };

    byTheNumbersSection = () => {
        return (
            <section className="by-the-numbers-section light-shadow">
                <h2 className="title">By the Numbers</h2>
                <div className="controlled-width">
                    {byTheNumbersSection.map((section) => {
                        const sectionKey = shortid();
                        return (
                            <section key={sectionKey} className="section">
                                <ResponsivePicture
                                    className="section-image"
                                    cloudinaryID={section.img}
                                    breakpoints={sectionImageBreakpoints}
                                />
                                <h3 className="section-title">
                                    {section.title}
                                </h3>
                                <div className="description">
                                    {section.description}
                                </div>
                            </section>
                        );
                    })}
                </div>
            </section>
        );
    };

    pressSection = () => {
        return (
            <section className="press-section dark-shadow">
                <FGBLabel className="black-font center-align">
                    Read about us in
                </FGBLabel>
                <PressLogoGrid logos={pressLogos} />
            </section>
        );
    };

    footerImageSection = () => {
        return (
            <section className="footer-image-section">
                <div className="flex-1-box">
                    <ResponsivePicture
                        cloudinaryID="/v1578690097/employer/marketing/Homepage_Footer_Image_1500x400.png"
                        breakpoints={headerFooterImageBreakpoints}
                        forceImageSize
                    />
                </div>
            </section>
        );
    };

    render() {
        const { handleDemoButtonClick } = this.props;
        return (
            <React.Fragment>
                {this.topImage()}
                {this.employerLogoSection()}
                {this.fgbHelpsEmployerSection()}
                {this.featuredVideoSection()}
                {this.marketingBlogSection()}
                {this.customerStoriesSection()}
                {this.byTheNumbersSection()}
                <CustomerQuotesSection
                    onDemoButtonClick={handleDemoButtonClick(
                        'employer-page-popup-click'
                    )}
                    title="Top Companies Love Fairygodboss"
                    buttonText="Learn more"
                />
                {this.pressSection()}
                {this.footerImageSection()}
            </React.Fragment>
        );
    }
}

export default (props) => (
    <div className="page-CompanyPages-EmployerLandingPage">
        <SnowplowPageViewTracker
            pageSection={PAGE_VIEW_SECTIONS.EMPLOYER_RESOURCES}
            pageType={PAGE_VIEW_TYPES.LANDING_PAGE}
            pageTab={PAGE_TABS.OVERVIEW}
        />
        <EmployerPageLayout
            className="pricing-page-layout"
            PAGE_PROPS={props.PAGE_PROPS}
            hasChangingContainer={false}
        >
            {({ handleDemoButtonClick }) => (
                <EmployerLandingPage
                    {...props}
                    handleDemoButtonClick={handleDemoButtonClick}
                />
            )}
        </EmployerPageLayout>
    </div>
);

EmployerLandingPage.propTypes = {
    /**
     * Company Logos
     * */
    companies: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string,
            alt: PropTypes.string,
            url: PropTypes.string,
        })
    ),
    /**
     * articles and reports section
     * */
    videoSrc: PropTypes.string,
    handleDemoButtonClick: PropTypes.func.isRequired,
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            contentUrl: PropTypes.string.isRequired,
            image: PropTypes.shape({
                src: PropTypes.string.isRequired,
                alt: PropTypes.string,
            }).isRequired,
            title: PropTypes.string.isRequired,
            marketingTag: PropTypes.shape({
                id: PropTypes.number.isRequired,
                name: PropTypes.string.isRequired,
                url: PropTypes.string.isRequired,
            }),
        })
    ),
};

EmployerLandingPage.defaultProps = {
    companies: [],
    resources: [],
    videoSrc: null,
};
